/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Button, TextField } from '@mui/material';
import React from 'react';
import { CustomDialog } from '@opengeoweb/shared';
import {
  validateViewPresetJson,
  validateWorkspacePresetJson,
} from '../../utils/clipboardUtils';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface PastePresetDialogProps {
  isOpen: boolean;
  mode: 'workspace' | 'viewpreset';
  onClose: () => void;
  onConfirm: (jsonString: string) => void;
}

export const PastePresetDialog: React.FC<PastePresetDialogProps> = ({
  isOpen,
  mode,
  onClose,
  onConfirm,
}: PastePresetDialogProps) => {
  const { t } = useWorkspaceTranslation();

  const [pastedText, setPastedText] = React.useState('');
  const [validationError, setValidationError] = React.useState<
    string | undefined
  >();

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setPastedText('');
      setValidationError(undefined);
    }
  }, [isOpen]);

  const validate = (value: string): string | undefined => {
    if (!value.trim()) {
      return undefined;
    }
    const result =
      mode === 'workspace'
        ? validateWorkspacePresetJson(value)
        : validateViewPresetJson(value);

    if (!result) {
      return undefined;
    }

    return result.errorParam
      ? t(result.error, { ERROR: result.errorParam })
      : t(result.error);
  };

  const handleChange = (value: string): void => {
    setPastedText(value);
    setValidationError(validate(value));
  };

  const handleSubmit = (): void => {
    const error = validate(pastedText);
    if (error) {
      setValidationError(error);
      return;
    }
    onConfirm(pastedText);
    onClose();
  };

  const isWorkspace = mode === 'workspace';
  const dialogTitle = isWorkspace
    ? t('workspace-paste-dialog-title')
    : t('workspace-paste-viewpreset-dialog-title');
  const confirmLabel = isWorkspace
    ? t('workspace-paste-load-button')
    : t('workspace-paste-load-viewpreset-button');
  const fieldLabel = isWorkspace
    ? t('workspace-paste-field-label')
    : t('workspace-paste-viewpreset-field-label');

  const isConfirmDisabled = !pastedText.trim() || Boolean(validationError);

  return (
    <CustomDialog
      actions={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            data-testid="paste-preset-cancel"
            onClick={onClose}
            variant="tertiary"
          >
            {t('workspace-cancel')}
          </Button>
          <Button
            data-testid="paste-preset-confirm"
            disabled={isConfirmDisabled}
            onClick={handleSubmit}
            variant="primary"
          >
            {confirmLabel}
          </Button>
        </Box>
      }
      data-testid="paste-preset-dialog"
      onClose={onClose}
      open={isOpen}
      sx={{ width: 800, height: 'auto', mx: 'auto' }}
      title={dialogTitle}
    >
      <TextField
        data-testid="paste-preset-textarea"
        error={Boolean(validationError)}
        fullWidth
        helperText={validationError}
        label={fieldLabel}
        multiline
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          const allowed = e.ctrlKey || e.metaKey || e.key === 'Tab';
          if (!allowed) {
            e.preventDefault();
          }
        }}
        rows={8}
        sx={{
          '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 12 },
        }}
        value={pastedText}
        variant="filled"
      />
    </CustomDialog>
  );
};
